<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { StepKey } from '@luogen/shared';
import { useSessionStore } from '@/stores/session';
import { useWorkflowStore } from '@/stores/workflow';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppRail from '@/components/layout/AppRail.vue';
import LoginDialog from '@/components/auth/LoginDialog.vue';

const route = useRoute();
const router = useRouter();
const session = useSessionStore();
const workflow = useWorkflowStore();

const loginVisible = ref(false);
const notice = ref('');

const pageTitle = computed(() => route.meta.title ?? '罗根智能体');
const showRail = computed(() => route.meta.rail !== false);
const activeStep = computed<StepKey>(() => {
  const fromMeta = route.meta.step as StepKey | undefined;
  return fromMeta ?? workflow.currentStep;
});

function openLogin() {
  loginVisible.value = true;
}

/** 左侧图标轨 = 工作流 7 步；有门槛的步骤会拦下并提示 */
function navigate(key: StepKey) {
  notice.value = '';
  const blocked = workflow.goTo(key);
  if (blocked) {
    notice.value = blocked;
    return;
  }
  void router.push(key === 'topic' ? '/topic-feed' : '/intelligent-agent');
}

function refresh() {
  window.location.reload();
}

onMounted(() => {
  session.hydrate();
  window.addEventListener('luogen:auth-required', openLogin);
});

onBeforeUnmount(() => {
  window.removeEventListener('luogen:auth-required', openLogin);
});
</script>

<template>
  <div class="app-shell">
    <AppHeader :page-title="pageTitle" :show-refresh="showRail" @refresh="refresh" />

    <div class="app-shell__body">
      <AppRail v-if="showRail" :active-step="activeStep" :account-label="session.account || '未登录'" :online="session.isLoggedIn" @navigate="navigate" @open-login="openLogin" />
      <main class="app-shell__main"><slot /></main>
    </div>

    <p v-if="notice" class="app-shell__notice" @click="notice = ''">{{ notice }}</p>
    <LoginDialog v-model="loginVisible" />
  </div>
</template>

<style scoped>
.app-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--lg-color-canvas);
}

.app-shell__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.app-shell__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.app-shell__notice {
  position: fixed;
  top: 64px;
  left: 50%;
  z-index: 40;
  margin: 0;
  padding: 10px 16px;
  transform: translateX(-50%);
  border: 1px solid var(--lg-color-border);
  border-radius: var(--lg-radius-pill);
  background: var(--lg-color-surface);
  box-shadow: var(--lg-shadow-md);
  font-size: 13px;
  cursor: pointer;
}
</style>
